import {
  addDaysYmd,
  formatAppDate,
  parseToInstant,
  todayYmd
} from '../../utils/appTimezone';

const RDV_SERVICE_LABELS = {
  CONSULTATION_MEDECIN: 'Consultation médicale',
  CONSULTATION_DENTAIRE: 'Consultation dentaire',
  SERVICE_INFIRMERIE: 'Infirmerie',
  AUTRE_SERVICE_MEDICAL: 'Autre service'
};

const CLOSED_RDV_STATUSES = new Set(['ANNULE', 'ABSENT', 'TERMINE']);

export function formatRdvServiceLabel(service) {
  if (!service) return '—';
  return RDV_SERVICE_LABELS[service] || String(service).replace(/_/g, ' ').toLowerCase();
}

export function formatRdvSlotDay(startAt) {
  const instant = parseToInstant(startAt);
  if (!instant) return formatAppDate(startAt);

  const slotDay = todayYmd(instant);
  const today = todayYmd();
  if (slotDay === today) return "Aujourd'hui";
  if (slotDay === addDaysYmd(today, 1)) return 'Demain';
  return formatAppDate(startAt);
}

export function isRdvToday(startAt) {
  const instant = parseToInstant(startAt);
  if (!instant) return false;
  return todayYmd(instant) === todayYmd();
}

export function isRdvClosed(status) {
  return CLOSED_RDV_STATUSES.has(String(status || '').toUpperCase());
}

export function isRdvEditable(rdv) {
  if (!rdv) return false;
  return !isRdvClosed(rdv.status);
}

const RDV_STATUS_ACTION_META = {
  CONFIRME: { label: 'Confirmer', variant: 'success' },
  TERMINE: { label: 'Terminer', variant: 'secondary' },
  ANNULE: { label: 'Annuler', variant: 'outline-danger', needsReason: true },
  ABSENT: { label: 'Marquer absent', variant: 'warning' }
};

const RDV_STATUS_TRANSITIONS = {
  EN_ATTENTE: ['CONFIRME', 'ANNULE', 'ABSENT'],
  CONFIRME: ['TERMINE', 'ANNULE', 'ABSENT'],
  TERMINE: [],
  ANNULE: [],
  ABSENT: []
};

export function getRdvStatusActions(status) {
  const key = String(status || 'EN_ATTENTE').toUpperCase();
  return (RDV_STATUS_TRANSITIONS[key] || [])
    .map((action) => ({
      action,
      ...(RDV_STATUS_ACTION_META[action] || { label: action, variant: 'outline-secondary' })
    }))
    .filter((entry) => entry.label);
}

export function canRegisterRdvVisit(rdv) {
  if (!rdv || rdv.reception_visit_id) return false;
  return !isRdvClosed(rdv.status);
}

export function rdvRowClassName(rdv) {
  const classes = ['recv-rdv-row'];
  if (isRdvToday(rdv?.start_at)) classes.push('is-today');
  if (String(rdv?.priority || '').toUpperCase() === 'URGENT') classes.push('is-urgent');
  if (isRdvClosed(rdv?.status)) classes.push('is-closed');
  if (rdv?.reception_visit_id) classes.push('is-registered');
  return classes.join(' ');
}

const RDV_HISTORY_ACTION_LABELS = {
  CREATE: 'Création du rendez-vous',
  UPDATE: 'Modification',
  STATUS_EN_ATTENTE: 'Statut : en attente',
  STATUS_CONFIRME: 'Statut : confirmé',
  STATUS_TERMINE: 'Statut : terminé',
  STATUS_ANNULE: 'Statut : annulé',
  STATUS_ABSENT: 'Statut : absent',
  REGISTER_VISIT: 'Passage enregistré à l’accueil'
};

export function formatRdvDoctorPreference(value) {
  if (value === 'SPECIFIQUE') return 'Praticien choisi';
  if (value === 'SANS_PREFERENCE') return 'Sans préférence';
  return value || '—';
}

export function formatRdvDoctorName(rdv) {
  if (!rdv) return '—';
  const name = `${rdv.doctor_first_name || ''} ${rdv.doctor_last_name || ''}`.trim();
  return name || '—';
}

export function formatRdvHistoryAction(entry) {
  if (!entry) return 'Événement';
  const key = String(entry.action || '').toUpperCase();
  if (RDV_HISTORY_ACTION_LABELS[key]) return RDV_HISTORY_ACTION_LABELS[key];
  if (key.startsWith('STATUS_')) {
    const status = key.replace('STATUS_', '');
    return `Statut : ${status.toLowerCase()}`;
  }
  return entry.action || 'Événement';
}

export function formatRdvHistoryActor(entry) {
  const name = `${entry?.actor_first_name || ''} ${entry?.actor_last_name || ''}`.trim();
  return name || 'Système';
}
